import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export type AppRole = 'admin' | 'care' | 'shift';
export type RoleStatus = 'idle' | 'loading' | 'ready' | 'error';

type AuthCtx = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  role: AppRole | null;
  roleStatus: RoleStatus;
  refreshRole: () => Promise<void>;
  signOut: () => Promise<void>;
};

const defaultAuthContext: AuthCtx = {
  session: null,
  user: null,
  loading: true,
  role: null,
  roleStatus: 'idle',
  refreshRole: async () => {},
  signOut: async () => {}
};

const AuthContext = createContext<AuthCtx>(defaultAuthContext);

type AuthProviderProps = {
  children: ReactNode;
};

const isAppRole = (value: unknown): value is AppRole =>
  value === 'admin' || value === 'care' || value === 'shift';

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [role, setRole] = useState<AppRole | null>(null);
  const [roleStatus, setRoleStatus] = useState<RoleStatus>('idle');

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!isMounted) return;
      setSession(data.session ?? null);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      setSession(currentSession ?? null);
    });

    return () => {
      isMounted = false;
      listener?.subscription.unsubscribe();
    };
  }, []);

  const userId = session?.user?.id ?? null;

  const loadRole = useCallback(async () => {
    if (!supabase || !userId) {
      setRole(null);
      setRoleStatus('idle');
      return;
    }

    setRoleStatus('loading');

    try {
      const { data, error } = await supabase
        .from('app_user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        // 役割テーブルが未作成の環境（マイグレーション適用前）では、
        // 既存の管理者が締め出されないよう admin として扱う。
        const code = (error as { code?: string }).code;
        if (code === '42P01' || code === 'PGRST205' || code === 'PGRST202') {
          console.warn('app_user_roles が未作成のため、暫定的に管理者として扱います');
          setRole('admin');
          setRoleStatus('ready');
          return;
        }
        console.error('役割の取得に失敗しました', error);
        setRole(null);
        setRoleStatus('error');
        return;
      }

      // 役割が未登録のアカウントは、労務を開けない care として扱う。
      const rawRole = data?.role;
      setRole(isAppRole(rawRole) ? rawRole : 'care');
      setRoleStatus('ready');
    } catch (err) {
      console.error('役割の取得に失敗しました', err);
      setRole(null);
      setRoleStatus('error');
    }
  }, [userId]);

  useEffect(() => {
    void loadRole();
  }, [loadRole]);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setRole(null);
    setRoleStatus('idle');
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      role,
      roleStatus,
      refreshRole: loadRole,
      signOut
    }),
    [session, loading, role, roleStatus, loadRole, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
